import { AnalysisEngine } from "../analysisEngine";

/**
 * Whether the session page should label this upload's frames on-device.
 *
 * Automatic, with one exception that matters: an upload whose engine is already named was CHOSEN
 * — either it already has client labels, or the uploader picked the GPU/Premium analysis at
 * upload time. Neither may be hijacked. Only an upload nothing has claimed is labelled here.
 */
export type ClientLabelDecision = "auto" | "none";

export function clientLabelDecision(state: {
  /** Labelling progress from the uploads list: confirmed label orders over all of them. */
  analysisPercentage: number;
  /** mediaStatus.analysisEngine / uploadFrames.analysisEngine — undefined until it loads. */
  analysisEngine: string | undefined;
  frameCount: number;
  isAnalysisReady: boolean;
  /** Two consecutive polls of the frame list agreed on its length. Label orders land one by
   * one after the split, and a list still filling in labels a partial clip (17816: 60 of ~67
   * frames, the tail never analysed). Omitted = treated as stable (callers that do not poll). */
  frameListStable?: boolean;
}): ClientLabelDecision {
  // Already analysed: there is nothing to label.
  if (state.isAnalysisReady) return "none";
  // The engine has not loaded yet — decide on the next render rather than guessing.
  if (state.analysisEngine === undefined) return "none";
  // Something already claimed this upload: client labels exist, or the GPU path was chosen.
  if (state.analysisEngine !== AnalysisEngine.None) return "none";
  // Not split yet: no frames to label.
  if (state.frameCount <= 0) return "none";
  // A labeller is part-way through this upload — never race it; let it finish.
  if (state.analysisPercentage > 0) return "none";
  // The frame list is still growing — wait for it to settle rather than label a partial clip.
  if (state.frameListStable === false) return "none";
  return "auto";
}
